import { IB_DESK_EMBED_PATH } from '@/hooks/useIbDeskSection';

const B2CORE_EMBED_PATH = '/embed';

const EMBED_SESSION_STORAGE_KEY = 'b2core_embed_session';

type B2CoreEmbedOutboundMessage = { type: 'embed-iframe-ready' } | { type: 'embed-request-jwt-token' };

type B2CoreEmbedInboundMessage =
  | { type: 'embed-theme-change'; theme: string }
  | { type: 'embed-language-change'; lang: string }
  | { type: 'embed-jwt-token'; token: string }
  | { type: 'embed-jwt-token-error'; error: unknown }
  | { type: 'embed-logout' };

export function isEmbedPath(pathname: string): boolean {
  return pathname === B2CORE_EMBED_PATH || pathname.startsWith(`${B2CORE_EMBED_PATH}/`);
}

export function crmOriginOf(clientPortalUrl: string): string | null {
  if (!clientPortalUrl) {
    return null;
  }

  try {
    return new URL(clientPortalUrl).origin;
  } catch {
    return null;
  }
}

export function postToCrm(message: B2CoreEmbedOutboundMessage, crmOrigin: string): void {
  window.parent.postMessage(message, crmOrigin);
}

export function readCrmMessage(event: MessageEvent, crmOrigin: string | null): B2CoreEmbedInboundMessage | null {
  if (crmOrigin === null || window.parent === window) {
    return null;
  }

  if (event.source !== window.parent || event.origin !== crmOrigin) {
    return null;
  }

  if (typeof event.data !== 'object' || event.data === null) {
    return null;
  }

  const data = event.data as Record<string, unknown>;

  switch (data.type) {
    case 'embed-theme-change':
      return typeof data.theme === 'string' ? { type: 'embed-theme-change', theme: data.theme } : null;
    case 'embed-language-change':
      return typeof data.lang === 'string' ? { type: 'embed-language-change', lang: data.lang } : null;
    case 'embed-jwt-token':
      return typeof data.token === 'string' ? { type: 'embed-jwt-token', token: data.token } : null;
    case 'embed-jwt-token-error':
      return { type: 'embed-jwt-token-error', error: data.error };
    case 'embed-logout':
      return { type: 'embed-logout' };
    default:
      return null;
  }
}

export function themeModeOf(theme: string): 'dark' | 'light' | null {
  if (theme === 'dark-theme') {
    return 'dark';
  }

  if (theme === 'light-theme') {
    return 'light';
  }

  return null;
}

export function safeReturnTo(value: string | null): string {
  try {
    const target = new URL(value ?? '', window.location.origin);
    const embedPagePath = target.pathname.replace(/\/+$/, '');
    const isEmbedPage = embedPagePath.startsWith(`${B2CORE_EMBED_PATH}/`);

    return target.origin === window.location.origin && isEmbedPage
      ? target.pathname + target.search
      : IB_DESK_EMBED_PATH;
  } catch {
    return IB_DESK_EMBED_PATH;
  }
}

export function embedEntryPath(returnTo: string): string {
  return `${B2CORE_EMBED_PATH}?return_to=${encodeURIComponent(returnTo)}`;
}

export function markEmbedSession(): void {
  try {
    sessionStorage.setItem(EMBED_SESSION_STORAGE_KEY, '1');
  } catch {}
}

function parentOrigin(): string | null {
  const ancestorOrigins = window.location.ancestorOrigins as DOMStringList | undefined;

  if (ancestorOrigins !== undefined && ancestorOrigins.length > 0) {
    return ancestorOrigins.item(0);
  }

  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

export function isEmbedSession(crmOrigin: string | null): boolean {
  if (crmOrigin === null || window.parent === window || parentOrigin() !== crmOrigin) {
    return false;
  }

  try {
    return sessionStorage.getItem(EMBED_SESSION_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
